/** @jsxImportSource @emotion/react */
import { IconButton, Menu, MenuItem } from '@mui/material';
import * as Icons from '@zip/icons';
import { ProductClassification } from 'enums';
import { theme } from 'global';
import * as productlogos from 'public/product-logos';
import { FC, ReactElement, useState } from 'react';
import * as styles from './HowItWorks.styles';
import { ApplicableProduct, ProductDetails } from './ProductTypesAndConstants';

interface ProductSelectProps {
  Current: ApplicableProduct;
  Products: ApplicableProduct[];
  onProductSelection: (p: ApplicableProduct) => void;
}

const ProductSelect: FC<ProductSelectProps> = (props: ProductSelectProps) => {
  const [menuAnchorPosition, setMenuAnchorPosition] = useState(null);
  const [currentProduct, setCurrentProduct] = useState<ApplicableProduct>(
    props.Current
  );

  return (
    <>
      <IconButton
        css={styles.iconButtonNoHover}
        onClick={(e): void => {
          const rect = e.currentTarget.getBoundingClientRect();
          setMenuAnchorPosition({
            top: rect.bottom, // Menu's top will be at the IconButton's bottom
            left: rect.left, // Menu's left will be at the IconButton's left
          });
        }}
      >
        {getMenuLogo(currentProduct, menuAnchorPosition !== null)}
      </IconButton>
      <Menu
        elevation={3}
        css={styles.menu}
        anchorReference="anchorPosition"
        anchorPosition={menuAnchorPosition}
        open={menuAnchorPosition !== null}
        onClose={(): void => setMenuAnchorPosition(null)}
      >
        {getMenuItems(
          currentProduct,
          props.Products,
          (p: ApplicableProduct) => {
            setCurrentProduct(p);
            setMenuAnchorPosition(null);
            props.onProductSelection(p);
          }
        )}
      </Menu>
    </>
  );
};

const getMenuItems = (
  current: ApplicableProduct,
  products: ApplicableProduct[],
  onClick: (ApplicableProduct) => void
): ReactElement[] => {
  const items = products.map((p, idx) => {
    return (
      <MenuItem
        css={styles.menuSpacing}
        key={idx}
        onClick={(): void => onClick(p)}
      >
        <div css={styles.menuItemBox}>
          <div className="body1">{ProductDetails.get(p).MenuTitle}</div>
          <div className="body2">{ProductDetails.get(p).MenuSubtitle}</div>
        </div>
        {current === p ? (
          <Icons.Check color={theme.colors.primary.medium} />
        ) : (
          <></>
        )}
      </MenuItem>
    );
  });

  return items;
};

const getMenuLogo = (
  product: ApplicableProduct,
  isMenuExpanded: boolean
): ReactElement => {
  const productClass = product as ProductClassification;

  const pathByClass = new Map<ProductClassification, string>([
    [ProductClassification.ZipPay, productlogos.zippay],
    [ProductClassification.ZipPlus, productlogos.zipplus],
    [ProductClassification.ZipMoney, productlogos.zipmoney],
  ]);

  return (
    <div css={styles.menuLogo}>
      <img src={pathByClass.get(productClass)} />
      {isMenuExpanded ? <Icons.ChevronUp /> : <Icons.ChevronDown />}
    </div>
  );
};

export default ProductSelect;
